import { useAtomValue, useSetAtom } from 'jotai';
import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { PREORDER_STATUS_DISPLAY_LABEL, PREORDERS_TABLE_TAB } from '@ekhodealer/ekho-common';
import {
	Badge,
	Button,
	ContentWithTooltip,
	Group,
	Stack,
	Tabs,
	Text,
	TextInput,
} from '@ekhodealer/ekho-common/rev';

import type { PreorderExternalStatus, PreordersTableTab } from '@ekhodealer/ekho-common';
import type { TabItem } from '@ekhodealer/ekho-common/rev';

import { ExportPopup } from '@common/components/ExportPopup/ExportPopup';
import { useCurrentServicesProviderId } from '@common/hooks/useCurrentServicesProviderId';
import { paths } from '@config/paths';

import { useExportPreorders } from '../api/useExportPreorders';
import { useSearchPreorders } from '../api/useSearchPreorders';
import { activeFiltersCountAtom } from '../atoms/preordersAppliedFiltersAtoms';
import {
	currentPageAtom,
	preordersParamsAtom,
	searchValueAtom,
	selectedPreordersAtom,
	updatePreordersTableTabFilterAtom,
	updateSearchValueAtom,
} from '../atoms/preordersAtoms';
import { usePreordersTableTabCounts } from '../hooks/usePreordersTableTabCounts';

import { PreordersFiltersDrawer } from './PreordersFiltersDrawer';
import { PreorderStats } from './PreorderStats';

/**
 * Formats preorders table tab for display.
 * Uses the shared PREORDER_STATUS_DISPLAY_LABEL map for status tabs;
 * the "all" tab has no corresponding status, so it's handled separately.
 */
function labelFromPreordersTableTab(tab: PreordersTableTab): string {
	if (tab === PREORDERS_TABLE_TAB.ALL) return 'All';
	const enumKey = tab.toUpperCase() as PreorderExternalStatus;
	return PREORDER_STATUS_DISPLAY_LABEL[enumKey] ?? '-';
}

// Map tab IDs to their tooltip content
const tabTooltipMap: Record<PreordersTableTab, string> = {
	[PREORDERS_TABLE_TAB.ALL]: 'All preorders, across every stage.',
	[PREORDERS_TABLE_TAB.PENDING_INVITATION]:
		'Preorders awaiting the buyer to be invited to complete checkout.',
	[PREORDERS_TABLE_TAB.AWAITING_CHECKOUT]:
		'Preorders where the buyer needs to complete the checkout process.',
	[PREORDERS_TABLE_TAB.IDLE]: 'Preorders with no recent activity.',
	[PREORDERS_TABLE_TAB.CONVERTED]: 'Preorders that have been successfully converted to orders.',
	[PREORDERS_TABLE_TAB.CANCELED]: 'Preorders that have been canceled.',
};

export function PreordersToolbar() {
	const navigate = useNavigate();
	const routeParams = useParams<{ tabId: PreordersTableTab }>();
	const servicesProviderId = useCurrentServicesProviderId();

	// Filter atoms
	const searchValue = useAtomValue(searchValueAtom);
	const updateSearchValue = useSetAtom(updateSearchValueAtom);
	const setCurrentPage = useSetAtom(currentPageAtom);

	// Export popup data
	const preordersParams = useAtomValue(preordersParamsAtom);
	const { data } = useSearchPreorders(preordersParams);
	const selectedPreorders = useAtomValue(selectedPreordersAtom);
	const exportMutation = useExportPreorders();

	// Get current active tab from URL params
	const activeTab = routeParams.tabId ?? PREORDERS_TABLE_TAB.ALL;

	// Sync tab filter atom with URL params (uses action atom that also clears selection)
	const updatePreorderTabFilter = useSetAtom(updatePreordersTableTabFilterAtom);
	useEffect(() => {
		const tabFromUrl = routeParams.tabId ?? PREORDERS_TABLE_TAB.ALL;
		updatePreorderTabFilter(tabFromUrl);
	}, [routeParams.tabId, updatePreorderTabFilter]);

	// Get tab counts using the hook
	const getTabCount = usePreordersTableTabCounts();

	// Get active filters count from atom
	const activeFiltersCount = useAtomValue(activeFiltersCountAtom);

	// Build tab items with tooltips and counts
	const tabItems: TabItem[] = Object.values(PREORDERS_TABLE_TAB).map(
		(tab: PreordersTableTab) => {
			const count = getTabCount(tab);
			return {
				id: tab,
				content: (
					<ContentWithTooltip
						content={
							<Group itemsSpacing='8' itemsAlignY='center' noWrap>
								<Group.Item>
									<span>{labelFromPreordersTableTab(tab)}</span>
								</Group.Item>
								<Group.Item>
									<Text size='footnote' color='secondary'>
										{count}
									</Text>
								</Group.Item>
							</Group>
						}
						tooltipContent={tabTooltipMap[tab]}
						tooltipProps={{ offset: 16 }}
					/>
				),
			};
		},
	);

	const handleTabSelect = (item: TabItem) => {
		if (!servicesProviderId) {
			return;
		}
		// Reset to page 1 when user clicks a tab
		setCurrentPage(1);
		navigate(paths.preordersTab.getHref(servicesProviderId, item.id as PreordersTableTab));
	};

	const handleSearchChange = (value: string) => {
		updateSearchValue(value);
	};

	const allFiltersButton = (
		<Button variant='secondary' size='medium'>
			All filters
		</Button>
	);

	return (
		<Stack itemsSpacing='16'>
			<PreorderStats />
			<Tabs items={tabItems} selectedKey={activeTab} onSelect={handleTabSelect} />
			<Group itemsAlignY='center' itemsAlignX='space-between' noWrap>
				<Group.Item>
					<TextInput
						placeholder='Search'
						type='search'
						size='small'
						value={searchValue}
						onChange={handleSearchChange}
					/>
				</Group.Item>
				<Group.Item>
					<Group itemsSpacing='8' noWrap>
						<ExportPopup
							resourceName='preorders'
							currentPageItems={data?.data ?? []}
							totalCount={data?.count ?? 0}
							selectedItems={selectedPreorders}
							getItemId={(item) => item.preorderId}
							onExport={(ids) =>
								exportMutation.mutateAsync({ preorderIds: ids })
							}
							isPending={exportMutation.isPending}>
							<Button variant='secondary' size='medium'>
								Export
							</Button>
						</ExportPopup>
						<PreordersFiltersDrawer
							trigger={
								activeFiltersCount > 0 ? (
									<Badge
										label={String(activeFiltersCount)}
										appearance='negative'
										variant='solid'
										size='small'>
										{allFiltersButton}
									</Badge>
								) : (
									allFiltersButton
								)
							}
						/>
					</Group>
				</Group.Item>
			</Group>
		</Stack>
	);
}
